import { createSignal, onMount, For, Show } from 'solid-js';
import { useSFetch } from '../hooks/sfetch';
import { useToast } from '../components/MessageBox';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { downloadFromApi } from '../services/downloadFile';

interface DisconnectEntry {
    id: string;
    botId: string;
    botUsername: string;
    createdAt: string;
    actionType: string;
    description: string;
    detailsJson: string | null;
}

const PAGE_SIZE = 50;

function actionIcon(a: string): string {
    switch (a) {
        case 'Disconnected': return 'fa-solid fa-plug-circle-xmark';
        case 'ErrorState': return 'fa-solid fa-triangle-exclamation';
        case 'PossessionEvicted': return 'fa-solid fa-user-slash';
        case 'PossessionInterrupted': return 'fa-solid fa-plug-circle-exclamation';
        default: return 'fa-solid fa-circle';
    }
}

function flattenDetails(json: string | null): [string, string][] {
    if (!json) return [];
    try {
        const obj = JSON.parse(json) as Record<string, unknown>;
        const out: [string, string][] = [];
        const push = (key: string, v: unknown) => {
            if (v === null || v === undefined || v === '' || v === false) return;
            if (Array.isArray(v)) { if (v.length > 0) out.push([key, v.join('\n')]); }
            else if (typeof v === 'object') { for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) push(`${key}.${k2}`, v2); }
            else out.push([key, typeof v === 'string' ? v : JSON.stringify(v)]);
        };
        for (const [k, v] of Object.entries(obj)) push(k, v);
        return out;
    } catch { return [['raw', json]]; }
}

export default function BotDisconnects(props: { kind: 'bot' | 'possession' }) {
    const sfetch = useSFetch();
    const toast = useToast();
    const { t } = useTranslation();

    const [entries, setEntries] = createSignal<DisconnectEntry[]>([]);
    const [total, setTotal] = createSignal(0);
    const [page, setPage] = createSignal(1);
    const [loading, setLoading] = createSignal(true);
    const [expanded, setExpanded] = createSignal<Set<string>>(new Set());

    async function load() {
        setLoading(true);
        const res = await sfetch(`/api/botengine/disconnects?kind=${props.kind}&page=${page()}&pageSize=${PAGE_SIZE}`);
        if (res.ok && res.value) {
            setEntries((res.value.entries ?? []) as DisconnectEntry[]);
            setTotal(res.value.total ?? 0);
        } else {
            toast?.error(t('STATE_ERROR_GENERIC'));
        }
        setLoading(false);
    }

    function goPage(p: number) { setPage(p); void load(); }

    function toggle(id: string) {
        const s = new Set(expanded());
        s.has(id) ? s.delete(id) : s.add(id);
        setExpanded(s);
    }

    async function exportAll() {
        const failed = await downloadFromApi(`/api/botengine/disconnects/export?kind=${props.kind}`, `${props.kind}-disconnects.json`);
        if (failed) toast?.error(t('STATE_ERROR_GENERIC'));
    }

    onMount(() => { void load(); });

    const totalPages = () => Math.max(1, Math.ceil(total() / PAGE_SIZE));

    return (
        <div class="v" style={{ 'max-width': '1100px', margin: '0 auto', padding: 'var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
            <div class="h" style={{ 'align-items': 'center', 'justify-content': 'space-between' }}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <h1 style={{ margin: 0 }}><Text val={props.kind === 'bot' ? 'BOT_DISCONNECTS_BOT_TITLE' : 'BOT_DISCONNECTS_POSSESSION_TITLE'} /></h1>
                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                        <Text val={props.kind === 'bot' ? 'BOT_DISCONNECTS_BOT_SUBTITLE' : 'BOT_DISCONNECTS_POSSESSION_SUBTITLE'} />
                    </span>
                </div>
                <button class="btn btn-secondary" onClick={() => void exportAll()}>
                    <i class="fa-solid fa-download" /> <Text val="BOT_DISCONNECTS_EXPORT" />
                </button>
            </div>

            <Show when={!loading()} fallback={
                <div class="fcard" style={{ 'text-align': 'center', color: 'var(--dark-text-muted)' }}><Text val="LOADING" /></div>
            }>
                <Show when={entries().length > 0} fallback={
                    <EmptyState icon="fa-plug-circle-xmark" title={t('BOT_DISCONNECTS_EMPTY')} />
                }>
                    <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                        <For each={entries()}>{(e) => (
                            <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                                <div class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-md)' }}>
                                    <i class={actionIcon(e.actionType)} style={{ color: 'var(--error-500)' }} />
                                    <div class="v" style={{ flex: '1', gap: 'var(--spacing-xs)', 'min-width': 0 }}>
                                        <div style={{ 'font-weight': 'var(--font-weight-semibold)' }}>
                                            {e.botUsername} <span style={{ color: 'var(--dark-text-muted)', 'font-weight': 'var(--font-weight-medium)' }}>· <Text val={`BOT_AUDIT_ACTION_${e.actionType.toUpperCase()}`} /></span>
                                        </div>
                                        <div style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)' }}>
                                            {formatDateTime(e.createdAt)}{e.description ? ` · ${e.description}` : ''}
                                        </div>
                                    </div>
                                    <Show when={e.detailsJson}>
                                        <button class="btn btn-secondary btn-sm" onClick={() => toggle(e.id)}>
                                            <i class={expanded().has(e.id) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'} />
                                        </button>
                                    </Show>
                                </div>
                                <Show when={expanded().has(e.id)}>
                                    <div class="v" style={{ gap: 'var(--spacing-xs)', 'font-size': 'var(--font-size-xs)', 'font-family': 'var(--font-family-mono)', background: 'var(--dark-bg-tertiary)', padding: 'var(--spacing-md)', 'border-radius': 'var(--radius-md)' }}>
                                        <For each={flattenDetails(e.detailsJson)}>{([k, v]) => (
                                            <div class="h" style={{ gap: 'var(--spacing-md)', 'align-items': 'flex-start' }}>
                                                <span style={{ color: 'var(--dark-text-muted)', 'min-width': '12rem' }}>{k}</span>
                                                <span style={{ 'white-space': 'pre-line', 'word-break': 'break-word' }}>{v}</span>
                                            </div>
                                        )}</For>
                                    </div>
                                </Show>
                            </div>
                        )}</For>
                    </div>

                    <Show when={totalPages() > 1}>
                        <div class="h" style={{ 'align-items': 'center', 'justify-content': 'center', gap: 'var(--spacing-md)' }}>
                            <button class="btn btn-secondary btn-sm" disabled={page() <= 1} onClick={() => goPage(page() - 1)}><Text val="BOT_DISCONNECTS_PREV" /></button>
                            <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>{page()} / {totalPages()}</span>
                            <button class="btn btn-secondary btn-sm" disabled={page() >= totalPages()} onClick={() => goPage(page() + 1)}><Text val="BOT_DISCONNECTS_NEXT" /></button>
                        </div>
                    </Show>
                </Show>
            </Show>
        </div>
    );
}
